import { useFloating, flip, autoUpdate, shift } from '@floating-ui/react';
import { enGB, nb } from 'date-fns/locale';
import * as React from 'react';
import { getContentLanguage } from '~/Features/Configuration/Utils/languageUtils';
import type { FormFieldProps } from '~/Partials/FormFields/FormFieldProps';
import { useClickOutsideRefObserver } from '~/Utils/Hooks/useClickOutsideObserver';
import type { BookMeetingCalendarExpanderTranslations } from './BookMeetingCalendarExpander/BookMeetingCalendarExpander';
import { BookMeetingCalendarExpander } from './BookMeetingCalendarExpander/BookMeetingCalendarExpander';
import type { BookMeetingDatePickerPropsTranslations } from './BookMeetingDatePicker/BookMeetingDatePicker';
import { BookMeetingDatePicker } from './BookMeetingDatePicker/BookMeetingDatePicker';

export interface BookMeetingCalendarTranslations
    extends BookMeetingCalendarExpanderTranslations,
        BookMeetingDatePickerPropsTranslations {
    labelFieldDate: string;
}

export interface BookMeetingCalendarProps extends FormFieldProps<Date | null> {
    translations: BookMeetingCalendarTranslations;
    required?: boolean;
    initialOpen?: boolean;
}

export const BookMeetingCalendar: React.FC<BookMeetingCalendarProps> = props => {
    const { translations, value: date, onChange, onBlur, required, initialOpen = false, ...inputProps } = props;

    const rootRef = React.useRef<HTMLDivElement>(null);
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(initialOpen);

    const { refs, floatingStyles } = useFloating({
        middleware: [flip(), shift()],
        whileElementsMounted: isCalendarOpen ? autoUpdate : undefined,
    });

    const handleActiveDayChange = (activeDay: Date | null) => activeDay && onChange(activeDay);

    const handleCalendarClose = () => setIsCalendarOpen(false);
    const handleCalendarCloseWithValueCheck = () => {
        date === null && isCalendarOpen && onChange(null);
        handleCalendarClose();
    };

    const handleOnBlur = (e: React.FocusEvent) => {
        !isCalendarOpen && onBlur && onBlur(e);
    };

    const handleCalendarExpansion = () => setIsCalendarOpen(!isCalendarOpen);

    useClickOutsideRefObserver(rootRef, handleCalendarCloseWithValueCheck);

    const language = getContentLanguage();
    const locale = language === 'no' ? nb : enGB;

    return (
        <div ref={rootRef}>
            <BookMeetingCalendarExpander
                selectedDate={date}
                onClick={handleCalendarExpansion}
                onBlur={handleOnBlur}
                setAnchorElement={refs.setReference}
                translations={translations}
                label={translations.labelFieldDate}
                locale={locale}
                required={required}
                {...inputProps}
            />
            <BookMeetingDatePicker
                activeDay={date}
                floatingStyles={floatingStyles}
                setFloatingElement={refs.setFloating}
                onClose={handleCalendarClose}
                onCloseWithCheck={handleCalendarCloseWithValueCheck}
                open={isCalendarOpen}
                translations={translations}
                changeActiveDay={handleActiveDayChange}
                locale={locale}
                rootRef={rootRef}
            />
        </div>
    );
};
